import { FooterService } from '../../../core/services/footer.service';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ErrorService } from '../../../core/services/error.service';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { MatTable, MatTableModule } from '@angular/material/table';
import { LocationService } from '../services/location.service';
import { LocationSummaryDto } from '../dtos/location-summary.dto';
import { LocationDto } from '../dtos/location.dto';
import { LocationModel } from '../models/location.model';
import { LocationMapper } from '../mappers/location.mapper';
import { LocationFormFactory } from '../mappers/location-form.factory';
import { LanguagePipe } from '../pipes/language.pipe';
import { AppointmentDistancePipe } from '../pipes/appointment-distance.pipe';
import { EmailNotificationSettingsPipe } from '../pipes/email-notification-settings.pipe';
import { EmailNotificationSettingEnum } from '../enums/email-notification-setting.enum';
import { AddLocationDialog } from '../dialogs/add-location/add-location.dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { OnlineBookingUrlDto } from '../dtos/online-booking-url.dto';
import { ManageLinkDialog } from '../dialogs/manage-link/manage-link.dialog';
import { LocalizedTextPipe } from '../../../shared/pipes/localized-text.pipe';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LocationOnlineBookingUrlsDto } from '../dtos/location-online-booking-urls.dto';
import { EmailSenderDto } from '../../customer/dtos/email-sender.dto';

@Component({
  selector: 'app-locations',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatIconModule,
    MatTableModule,
    LanguagePipe,
    AppointmentDistancePipe,
    EmailNotificationSettingsPipe,
    MatTabsModule,
    LocalizedTextPipe,
    TranslocoPipe
  ],
  templateUrl: './location.component.html',
  styleUrl: './location.component.scss'
})
export class LocationComponent implements OnInit {
  @ViewChild('onlineBookingUrlsTable') table!: MatTable<any>;

  locationSelector!: FormControl;
  locationSummaryDtos!: LocationSummaryDto[];
  locationForm!: FormGroup;
  onlineBookingUrlsForm!: FormArray;
  imagePreview?: string;
  imageError?: string;
  emailSenders: EmailSenderDto[] = [];
  useMailing: boolean = false;
  selectedSender?: EmailSenderDto;

  protected readonly EmailNotificationSettingEnum = EmailNotificationSettingEnum;

  constructor(private locationService: LocationService, private translocoService: TranslocoService, private errorService: ErrorService, private footerService: FooterService, private dialog: MatDialog, private snackBar: MatSnackBar, private fb: FormBuilder) {
  }

  get bookingHint(): FormArray {
    return this.locationForm.get('bookingHint') as FormArray;
  }

  get confirmationText(): FormArray {
    return this.locationForm.get('confirmationText') as FormArray;
  }

  ngOnInit(): void {
    this.loadLocationSummaries();
  }

  loadLocationSummaries(loadLast = false): void {
    this.locationService.getLocationSummaries().subscribe({
      next: (locationSummaryDtos: LocationSummaryDto[]) => {
        this.locationSummaryDtos = locationSummaryDtos;

        let index;
        if (loadLast) {
          index = locationSummaryDtos.length - 1;
        } else {
          index = 0;
        }

        let defaultLocationId = locationSummaryDtos[index].locationId ?? 0;

        this.locationSelector = new FormControl(defaultLocationId);
        this.loadLocation(defaultLocationId);
      },
      error: (error) => {
        this.errorService.handleError(error, this.translocoService.translate('error.title.locations'));
      }
    });
  }

  loadLocation(locationId: number): void {
    this.locationService.getLocation(locationId).subscribe({
      next: (locationDto: LocationDto) => {
        let locationModel: LocationModel = LocationMapper.toModel(locationDto);

        this.imagePreview = locationModel.imageUrl;
        if(locationDto.emailSettings.useMailing){
          this.useMailing = true;
          this.emailSenders = locationDto.emailSettings.emailSenders ?? [];
          this.emailSenders.unshift({ senderName: this.translocoService.translate('label.location.customer_default'), email: undefined });
          this.selectedSender = this.emailSenders.find(p => p.email === locationDto.emailSettings.senderEmail);
          if (!this.selectedSender) {
            this.selectedSender = this.emailSenders[0];
          }
        }

        this.locationForm = LocationFormFactory.createLocationForm(this.fb, locationModel);

        this.locationForm.valueChanges.subscribe(() => {
          this.footerService.showFooter(() => this.updateLocation());
        });
      },
      error: (error) => {
        this.errorService.handleError(error, this.translocoService.translate('error.title.location'));
      }
    });

    this.locationService.getOnlineBookingUrls(locationId).subscribe({
      next: (locationOnlineBookingUrlsDto: LocationOnlineBookingUrlsDto) => {
        this.onlineBookingUrlsForm = this.fb.array(locationOnlineBookingUrlsDto.onlineBookingUrls?.map(url => LocationFormFactory.createOnlineBookingUrlForm(this.fb, url)) || []);
      },
      error: (error) => {
        this.errorService.handleError(error, this.translocoService.translate('error.title.online_booking_urls'));
      }
    });
  }

  changeLocation(event: any): void {
    this.footerService.hideFooter();

    let locationId: number = event.value;
    this.loadLocation(locationId);
  }

  updateLocation(): boolean {
    if (this.locationForm.invalid) {
      this.locationForm.markAllAsTouched();
      return false;
    }

    let locationModel: LocationModel = this.locationForm.value;
    let locationDto: LocationDto = LocationMapper.toDto(locationModel);

    this.locationService.updateLocation(locationDto).subscribe({
      next: () => {
        this.snackBar.open(this.translocoService.translate('label.saved'), 'OK', {
          duration: 3000,
          horizontalPosition: 'end',
          verticalPosition: 'bottom'
        });
      },
      error: (errorDto: HttpErrorResponse) => {
        this.errorService.handleError(errorDto, this.translocoService.translate('error.title.edit_location'));
      }
    });
    return true;
  }

  addLocation(): void {
    this.dialog
      .open(AddLocationDialog, { autoFocus: false })
      .afterClosed()
      .subscribe((reload) => {
        if (reload) {
          this.loadLocationSummaries(true);
        }
      });
  }

  editOnlineBookingUrl(onlineBookingForm: FormGroup) {
    this.dialog
      .open(ManageLinkDialog, { autoFocus: false, data: onlineBookingForm.value })
      .afterClosed()
      .subscribe((onlineBookingUrlDto: OnlineBookingUrlDto) => {
        if (onlineBookingUrlDto) {
          onlineBookingForm.patchValue(onlineBookingUrlDto);
          this.table.renderRows();
          this.updateLinks();
        }
      });
  }

  deleteOnlineBookingUrl(onlineBookingForm: FormGroup) {
    let index = this.onlineBookingUrlsForm.controls.indexOf(onlineBookingForm);
    if (index >= 0) {
      this.onlineBookingUrlsForm.removeAt(index);
      this.table.renderRows();
      this.updateLinks();
    }
  }


  addOnlineBookingUrl(): void {
    this.dialog
      .open(ManageLinkDialog, { autoFocus: false })
      .afterClosed()
      .subscribe((onlineBookingUrlDto: OnlineBookingUrlDto) => {
        if (onlineBookingUrlDto) {
          this.onlineBookingUrlsForm.push(LocationFormFactory.createOnlineBookingUrlForm(this.fb, onlineBookingUrlDto));
          this.table.renderRows();
          this.updateLinks();
        }
      });
  }

  updateLinks() {
    let locationModel: LocationModel = this.locationForm.value;
    let locationDto: LocationDto = LocationMapper.toDto(locationModel);

    let onlineBookingUrls: OnlineBookingUrlDto[] = this.onlineBookingUrlsForm.value;

    var requestDto = { locationId: locationDto.locationId, onlineBookingUrls: onlineBookingUrls };
    this.locationService.updateOnlineBookingUrls(requestDto).subscribe({
      next: () => {
        this.snackBar.open(this.translocoService.translate('label.saved'), 'OK', {
          duration: 3000,
          horizontalPosition: 'end',
          verticalPosition: 'bottom'
        });
      },
      error: (errorDto: HttpErrorResponse) => {
        this.errorService.handleError(errorDto, this.translocoService.translate('error.title.edit_location'));
      }
    });
  }

  uploadImage(event: Event): void {
    const input : HTMLInputElement = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];

    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        //image dimension check
        const maxDimension = 1500;
        if (img.width > maxDimension || img.height > maxDimension) {
          this.imageError = this.translocoService.translate('error.image.dimension', {width: img.width, height: img.height, maxDimension: maxDimension});
          input.value = '';
          this.imagePreview = undefined;
          return;
        }

        //image size check
        const maxSize = 1024 * 1024;
        if (file.size > maxSize) {
          this.imageError = this.translocoService.translate('error.image.size', {size: (file.size / 1024 / 1024).toFixed(1), maxSize: 1});
          input.value = '';
          this.imagePreview = undefined;
          return;
        }

        const formData = new FormData();
        formData.append('imageFile', file, file.name);

        this.locationService
          .addLocationImage(this.locationSelector.value, formData)
          .subscribe();

        this.imagePreview = reader.result as string;
        this.imageError = undefined;
      };

      img.src = reader.result as string;
    };

    reader.readAsDataURL(file);
  }

  deleteImage(): void {
    this.imageError = undefined;
    this.locationService.deleteLocationImage(this.locationSelector.value).subscribe({
      next: () => {
        this.imagePreview = undefined;
      },
      error: (error) => {
        this.errorService.handleError(error, this.translocoService.translate('error.title.delete_image'));
      }
    });
  }

  changeSender(value: any) {
    this.selectedSender = value.value;
    if (!this.selectedSender?.email) {
      this.locationForm.patchValue({senderEmail: undefined, senderName: undefined});
    }
    else{
      this.locationForm.patchValue({senderEmail: this.selectedSender?.email, senderName: this.selectedSender?.senderName});
    }
  }

  compareSenders(o1: any, o2: any): boolean {
    return o1 && o2 ? (o1.email === o2.email) : o1 === o2;
  }
}
